import React, { useState } from 'react';
import {
  Plus, Search, Filter, Calendar, MapPin, DollarSign, Users,
  Eye, Edit, Trash2, CheckCircle, Clock, AlertCircle, Star,
  Briefcase, Target, Zap, Award, MessageSquare, Send,
  TrendingUp, BarChart3, Globe, Leaf, Heart, Shield
} from 'lucide-react';

const Projects = ({ company }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  // Mock data pour les projets
  const projects = [
    {
      id: 1,
      title: "Refonte UX/UI App Mobile Éco-responsable",
      description: "Redesign complet de notre application mobile avec focus sur l'empreinte carbone et l'accessibilité",
      status: 'active',
      budget: 8500,
      applications: 23,
      deadline: '2024-02-15',
      skills: ['UX/UI Design', 'Figma', 'Eco-design', 'Accessibilité'],
      type: 'Design',
      location: 'Remote',
      duration: '6 semaines',
      certificationRequired: 'Label NR',
      impact: 'Réduction de 30% de l\'empreinte carbone',
      urgency: 'medium',
      createdAt: '2024-01-10'
    },
    {
      id: 2,
      title: "Développement Dashboard Analytics RSE",
      description: "Création d'un tableau de bord pour suivre nos indicateurs RSE et ODD",
      status: 'completed',
      budget: 12000,
      applications: 45,
      deadline: '2024-01-30',
      skills: ['React', 'Node.js', 'Data Viz', 'API'],
      type: 'Développement',
      location: 'Hybride Paris',
      duration: '8 semaines',
      certificationRequired: 'B-Corp',
      impact: 'Amélioration du suivi RSE',
      urgency: 'high',
      createdAt: '2023-12-05',
      freelancer: 'Thomas Bernard',
      rating: 4.9
    },
    {
      id: 3,
      title: "Stratégie Marketing Digital Responsable",
      description: "Élaboration d'une stratégie marketing digital alignée avec nos valeurs environnementales",
      status: 'draft',
      budget: 6500,
      applications: 0,
      deadline: '2024-03-01',
      skills: ['Marketing Digital', 'SEO', 'Growth Hacking', 'Communication'],
      type: 'Marketing',
      location: 'Remote',
      duration: '4 semaines',
      certificationRequired: 'Carbon Trust',
      impact: 'Communication responsable',
      urgency: 'low',
      createdAt: '2024-01-12'
    }
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'text-blue-600 bg-blue-100';
      case 'completed': return 'text-green-600 bg-green-100';
      case 'draft': return 'text-gray-600 bg-gray-100';
      case 'paused': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getUrgencyColor = (urgency) => {
    switch(urgency) {
      case 'high': return 'text-red-500';
      case 'medium': return 'text-orange-500';
      case 'low': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTab = activeTab === 'all' || project.status === activeTab;
    const matchesFilter = selectedFilter === 'all' || project.type.toLowerCase() === selectedFilter;
    
    return matchesSearch && matchesTab && matchesFilter;
  });

  const ProjectCard = ({ project }) => (
    <div className="bg-white rounded-2xl border border-gray-100 hover:shadow-2xl transition-all duration-300 overflow-hidden group hover:scale-[1.02]">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-lg font-bold text-gray-900 group-hover:text-green-600 transition-colors duration-200">
                {project.title}
              </h3>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(project.status)}`}>
                {project.status === 'active' ? 'Actif' : 
                 project.status === 'completed' ? 'Terminé' : 
                 project.status === 'draft' ? 'Brouillon' : project.status}
              </span>
            </div>
            <p className="text-gray-600 text-sm mb-3 line-clamp-2">{project.description}</p>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${getUrgencyColor(project.urgency)}`}></div>
            <div className="flex space-x-1">
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                <Eye className="w-4 h-4 text-gray-600" />
              </button>
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                <Edit className="w-4 h-4 text-gray-600" />
              </button>
              <button className="p-2 hover:bg-red-100 rounded-lg transition-colors duration-200">
                <Trash2 className="w-4 h-4 text-red-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="text-center">
            <div className="text-lg font-bold text-gray-900">{project.budget.toLocaleString()}€</div>
            <div className="text-xs text-gray-500">Budget</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-blue-600">{project.applications}</div>
            <div className="text-xs text-gray-500">Candidatures</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-purple-600">{project.duration}</div>
            <div className="text-xs text-gray-500">Durée</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-green-600">{project.location}</div>
            <div className="text-xs text-gray-500">Localisation</div>
          </div>
        </div>

        {/* Skills */}
        <div className="flex flex-wrap gap-2 mb-4">
          {project.skills.map((skill) => (
            <span key={skill} className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-lg">
              {skill}
            </span>
          ))}
        </div>

        {/* Impact & Certification */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Leaf className="w-4 h-4 text-green-500" />
            <span className="text-sm text-gray-600">{project.impact}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Award className="w-4 h-4 text-yellow-500" />
            <span className="text-sm font-medium text-gray-700">{project.certificationRequired}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          {project.status === 'active' && (
            <button className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-200 flex items-center justify-center">
              <Users className="w-4 h-4 mr-2" />
              Voir candidatures
            </button>
          )}
          {project.status === 'completed' && (
            <button className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 flex items-center justify-center">
              <Star className="w-4 h-4 mr-2" />
              Évaluer ({project.freelancer})
            </button>
          )}
          {project.status === 'draft' && (
            <button className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 flex items-center justify-center">
              <Send className="w-4 h-4 mr-2" />
              Publier
            </button>
          )}
          <button className="px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors duration-200">
            <MessageSquare className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
            <Briefcase className="w-8 h-8 mr-3 text-green-500" />
            Mes Projets
          </h1>
          <p className="text-gray-600">
            Gérez vos projets éthiques et trouvez les talents responsables qui partagent vos valeurs.
          </p>
        </div>
        
        <div className="mt-4 lg:mt-0 flex items-center space-x-4">
          <button 
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-200 flex items-center"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nouveau projet
          </button>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Rechercher un projet..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-4">
            <select 
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="all">Tous les types</option>
              <option value="design">Design</option>
              <option value="développement">Développement</option>
              <option value="marketing">Marketing</option>
              <option value="conseil">Conseil</option>
            </select>

            <button className="flex items-center space-x-2 px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors duration-200">
              <Filter className="w-4 h-4 text-gray-600" />
              <span className="text-gray-700">Filtres</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mt-6 bg-gray-100 rounded-xl p-1">
          {[
            { id: 'all', label: 'Tous', count: projects.length },
            { id: 'active', label: 'Actifs', count: projects.filter(p => p.status === 'active').length },
            { id: 'completed', label: 'Terminés', count: projects.filter(p => p.status === 'completed').length },
            { id: 'draft', label: 'Brouillons', count: projects.filter(p => p.status === 'draft').length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 px-4 rounded-lg font-semibold text-sm transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-white text-green-600 shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-600 text-sm font-medium">Projets Actifs</p>
              <p className="text-2xl font-bold text-green-700">{projects.filter(p => p.status === 'active').length}</p>
            </div>
            <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 text-sm font-medium">Candidatures</p>
              <p className="text-2xl font-bold text-blue-700">{projects.reduce((acc, p) => acc + p.applications, 0)}</p>
            </div>
            <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-600 text-sm font-medium">Budget Total</p>
              <p className="text-2xl font-bold text-purple-700">{projects.reduce((acc, p) => acc + p.budget, 0).toLocaleString()}€</p>
            </div>
            <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl p-6 border border-orange-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-600 text-sm font-medium">Taux de Réussite</p>
              <p className="text-2xl font-bold text-orange-700">94%</p>
            </div>
            <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
              <Target className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="space-y-6">
        {filteredProjects.length > 0 ? (
          filteredProjects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))
        ) : (
          <div className="text-center py-12">
            <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun projet trouvé</h3>
            <p className="text-gray-600 mb-6">Créez votre premier projet éthique pour commencer à collaborer avec des freelances responsables.</p>
            <button 
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-200"
            >
              <Plus className="w-5 h-5 mr-2 inline" />
              Créer un projet
            </button>
          </div>
        )}
      </div>

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900">Créer un Nouveau Projet</h2>
              <p className="text-gray-600 mt-1">Partagez votre vision et trouvez le freelance parfait pour votre mission éthique.</p>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Message d'engagement */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
                <div className="flex items-start space-x-3">
                  <Heart className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-green-700">Projet Responsable</h4>
                    <p className="text-green-600 text-sm mt-1">
                      Votre projet sera uniquement visible par des freelances certifiés et engagés dans une démarche éthique.
                    </p>
                  </div>
                </div>
              </div>

              {/* Form would go here */}
              <div className="text-center py-8">
                <p className="text-gray-500">Formulaire de création de projet à implémenter...</p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 flex justify-end space-x-3">
              <button 
                onClick={() => setShowCreateModal(false)}
                className="px-6 py-3 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              >
                Annuler
              </button>
              <button className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-200">
                Publier le projet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;